import { useEffect, useState, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useReadingStatus } from "@features/mx/routeManagement/hooks";
import { toast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useUploadReading } from "@features/mx/device-management/hooks";
import { useParams } from "react-router-dom";
import { queryClient } from "@shared/api/queries/queryClient";

interface UploadReadingModalProps {
  isOpen: boolean;
  onClose: () => void;
  meter: any;
}

export function UploadReadingModal({ isOpen, onClose, meter }: UploadReadingModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: readingStatusData } = useReadingStatus();
  const remoteUtilityId = useRemoteUtilityId();
  const { id } = useParams();
  const { mutateAsync: uploadReading } = useUploadReading(id);

  // ✅ Compute options after data arrives
  const readingStatusOptions =
    readingStatusData?.result?.map((readingStatus) => ({
      value: readingStatus.key,
      label: readingStatus.value,
    })) || [];

  const lastReading = meter?.lastReading || "";
  const lastReadingDate = meter?.lastReadingDate || "";

  // ✅ Build form fields AFTER data is available
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "previousReading",
        label: "Previous Reading",
        type: "number",
        disabled: true,
            required: true,
        defaultValue: lastReading,
        className: "col-span-1",
      },
      {
        name: "previousReadingDate",
        label: "Previous Reading Date",
        type: "text",
        disabled: true,
            required: true,
        defaultValue: lastReadingDate,
        className: "col-span-1",
      },
      {
        name: "currentReading",
        label: "Current Reading",
        type: "decimal",
        required: true,
        className: "col-span-1",
        placeholder:"e.g.12345"
      },
      {
        name: "currentReadingDate",
        label: "Current Reading Date",
        type: "datepicker",
        required: true,
        className: "col-span-1",
      },
      {
        name: "readingStatus",
        label: "Reading Status",
        type: "select",
        options: readingStatusOptions,
        className: "col-span-1",
          required: true,
      },
      {
        name: "photoUpload",
        label: "Photo Upload",
        accept: ".png,.jpeg",
        type: "file",
        className: "col-span-2",
        helperText:"Attach a photo of the meter dial for verification.(Accepted formats: .png,  .jpeg)"

      },
      {
        name: "remarks",
        label: "Remarks",
        type: "textarea",
        className: "col-span-2",
        placeholder:"Optional text area for additional notes"
      },
    ],
    [readingStatusOptions, lastReading, lastReadingDate]
  );

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
  });

  // ✅ Update default values once meter changes
  useEffect(() => {
    if (meter) {
      form.reset({
        previousReading: meter.lastReading || "",
        previousReadingDate: meter.lastReadingDate || "",
      });
    }
  }, [meter, form]);

const handleSubmit = async (data: any) => {
  try {
    // ✅ Check if current reading date < previous reading date
    const prevDate = new Date(data.previousReadingDate);
    const currDate = new Date(data.currentReadingDate);

    if (currDate < prevDate) {
      toast({
        title: "Invalid Date Selection",
        description: "Current Reading Date cannot be earlier than Previous Reading Date.",
        variant: "destructive",
      });
      return; 
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("remote_utility_id", remoteUtilityId);
    formData.append("previous_reading", data.previousReading);
    formData.append("previous_reading_date", data.previousReadingDate);
    formData.append("current_reading", data.currentReading);
    formData.append("current_reading_date", data.currentReadingDate);

    if (data.readingStatus) formData.append("reading_status", data.readingStatus);
    if (data.remarks) formData.append("remark", data.remarks);
    if (data.photoUpload?.length > 0) formData.append("file", data.photoUpload[0]);

    await uploadReading(formData);

    toast({
      title: "Reading Uploaded",
      description: "The meter reading has been uploaded successfully.",
    });

    queryClient.invalidateQueries({
      queryKey: ['mx', 'device', Number(id)],
    });

    form.reset();
    onClose();
  } catch (err) {
    console.error("Upload failed:", err);
    toast({
      title: "Error",
      description: "Failed to upload the reading. Please try again.",
      variant: "destructive",
    });
  } finally {
    setIsSubmitting(false);
  }
};


  const handleCancel = () => {
    form.reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Reading</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-200px)] px-1">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleCancel}
            gridColumns={2}
            config={{
              showSubmitButton: false,
              showResetButton: false,
              layout: "vertical",
              spacing: "comfortable",
              classes: FIELD_CLASSES,
            }}
            renderCustomFooter={() => (
              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="button" onClick={form.handleSubmit(handleSubmit)} disabled={isSubmitting}>
                  {isSubmitting ? "Uploading..." : "Upload"}
                </Button>
              </div>
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
